import { forwardRef, useId } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Badge } from '../primitives/Badge';
import { Button } from '../primitives/Button';
import { Icon } from '../primitives/Icon';
import { Input } from '../primitives/Input';
import { MultiSelect } from '../primitives/MultiSelect';
import { PeriodSelector, type PeriodValue } from '../primitives/PeriodSelector';
import { Select, type SelectOption } from '../primitives/Select';

export type ComparisonMode = 'none' | 'priorPeriod' | 'priorYear' | 'scenario';

export const COMPARISON_LABELS: Record<ComparisonMode, string> = {
  none: 'No comparison',
  priorPeriod: 'vs prior period',
  priorYear: 'vs prior year',
  scenario: 'vs scenario',
};

export interface FilterGroup {
  id: string;
  label: string;
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
}

export interface AppliedFilterChip {
  id: string;
  label: string;
  onRemove: () => void;
}

export interface FilterBarProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onChange'> {
  period?: {
    value: PeriodValue;
    onChange: (value: PeriodValue) => void;
    readOnlyReason?: string | null;
  };
  scope?: {
    label?: string;
    options: SelectOption[];
    value: string;
    onChange: (value: string) => void;
    /** Set when the caller's span of control is narrower than the selection. */
    narrowedNote?: string | null;
  };
  comparison?: {
    value: ComparisonMode;
    onChange: (value: ComparisonMode) => void;
    scenarioOptions?: SelectOption[];
    scenarioValue?: string;
    onScenarioChange?: (value: string) => void;
  };
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  groups?: FilterGroup[];
  /** Currently applied filters, rendered as removable chips. */
  appliedChips?: AppliedFilterChip[];
  onClearAll?: () => void;
  actions?: React.ReactNode;
  density?: 'comfortable' | 'compact';
}

/**
 * Presentation only. URL persistence, debouncing and query invalidation belong
 * to the app: this component holds no state beyond what its callers pass in.
 */
export const FilterBar = forwardRef<HTMLElement, FilterBarProps>(function FilterBar(
  {
    period,
    scope,
    comparison,
    search,
    groups,
    appliedChips,
    onClearAll,
    actions,
    density = 'comfortable',
    className,
    ...rest
  },
  ref,
) {
  const baseId = useId();
  const hasChips = Boolean(appliedChips?.length);

  return (
    <section
      ref={ref}
      aria-label="Filters"
      className={cn(
        'flex flex-col gap-3 rounded-xl border border-line bg-surface shadow-card',
        density === 'compact' ? 'p-3' : 'p-4',
        className,
      )}
      {...rest}
    >
      <div className="flex flex-wrap items-end gap-3">
        {search ? (
          <Input
            label="Search"
            hideLabel
            type="search"
            iconStart="search"
            value={search.value}
            placeholder={search.placeholder ?? 'Search…'}
            onChange={(event) => search.onChange(event.target.value)}
            containerClassName="min-w-[240px] flex-1"
          />
        ) : null}

        {period ? (
          <PeriodSelector
            value={period.value}
            onChange={period.onChange}
            readOnlyReason={period.readOnlyReason ?? null}
          />
        ) : null}

        {scope ? (
          <div className="flex flex-col gap-1">
            <Select
              label={scope.label ?? 'Scope'}
              options={scope.options}
              value={scope.value}
              onChange={(event) => scope.onChange(event.target.value)}
            />
            {scope.narrowedNote ? (
              <span className="flex items-center gap-1 text-dense text-content-muted">
                <Icon name="info" size={12} />
                {scope.narrowedNote}
              </span>
            ) : null}
          </div>
        ) : null}

        {comparison ? (
          <>
            <Select
              label="Compare"
              options={(Object.keys(COMPARISON_LABELS) as ComparisonMode[]).map((mode) => ({
                value: mode,
                label: COMPARISON_LABELS[mode],
              }))}
              value={comparison.value}
              onChange={(event) => comparison.onChange(event.target.value as ComparisonMode)}
            />
            {comparison.value === 'scenario' && comparison.scenarioOptions ? (
              <Select
                label="Scenario"
                options={comparison.scenarioOptions}
                value={comparison.scenarioValue ?? ''}
                placeholder="Choose a scenario"
                onChange={(event) => comparison.onScenarioChange?.(event.target.value)}
              />
            ) : null}
          </>
        ) : null}

        {groups?.map((group) => (
          <MultiSelect
            key={group.id}
            id={`${baseId}-${group.id}`}
            label={group.label}
            options={group.options}
            value={group.value}
            onChange={group.onChange}
            className="min-w-[170px]"
          />
        ))}

        {actions ? <div className="ml-auto flex items-end gap-2">{actions}</div> : null}
      </div>

      {hasChips ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-line-subtle pt-3">
          <span className="text-dense font-semibold uppercase tracking-eyebrow text-content-muted">
            Applied
          </span>
          {appliedChips?.map((chip) => (
            <span key={chip.id} className="inline-flex">
              <Badge tone="info" icon="filter" className="pr-1">
                {chip.label}
                <button
                  type="button"
                  onClick={chip.onRemove}
                  aria-label={`Remove filter ${chip.label}`}
                  className={cn('ml-1 rounded-sm p-0.5 hover:bg-surface', focusRing)}
                >
                  <Icon name="x" size={10} strokeWidth={2.5} />
                </button>
              </Badge>
            </span>
          ))}
          {onClearAll ? (
            <Button variant="ghost" size="sm" onClick={onClearAll}>
              Clear all
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
});
